import React, { useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Toaster, toast } from "react-hot-toast";
import { Base_URL } from "../../actions/Base_URL";

export default function Login() {
  const { user_id } = useParams();
  const location = useLocation();
  let loginUserId = location.pathname.split("/login/")[1];
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState(location.state?.role || "student");
  const [formData, setFormData] = useState({
    id: loginUserId,
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  function getFormData(e) {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  }

  function validateForm() {
    let errors = {};
    let isValid = true;

    if (!formData.email) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Enter a valid email address";
      isValid = false;
    }

    if (!formData.password) {
      errors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  }

  async function submitFormData(e) {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(`${Base_URL}/user/signin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          role: role,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.message === "email is not verified") {
          toast.error("Email not verified. Please verify your email to continue.");
          navigate('/auth/verify/otp', {
            state: {
              email: formData.email,
              source: 'login',
              message: 'Please verify your email to complete login',
              error: 'Email is not verified'
            }
          });
          return;
        }
        throw new Error(data.message || "Something went wrong");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user_id", data.user_id);
      if (data.user_name) localStorage.setItem("user_name", data.user_name);
      if (data.club_details) localStorage.setItem("club_name", data.club_details.club_name);
      if (data.club_details) localStorage.setItem("club_id", data.club_details.club_id);
      if (data.lab) localStorage.setItem("lab_id", data.lab);

      const decoded = jwtDecode(data.token);
      const decodedRole = decoded.role?.toLowerCase();

      toast.success("Login Successful!");
      setFormData({ email: "", password: "" });
      setTimeout(() => {
        if (decodedRole === "admin") {
          navigate("/admin/events");
        } else if (decodedRole === "student") {
          navigate("/student/events");
        } else if (decodedRole === "labincharge") {
          navigate("/labincharge/inventory");
        } else {
          navigate("/");
        }
      }, 1000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Toaster position="bottom-left" reverseOrder={false} />
      <main>
        <section className="relative w-full sm:py-3 min-h-screen bg-dark-primary">
          <div
            className="relative w-full min-h-screen flex items-center justify-center bg-dark-primary"
          >
            <div
              className="absolute top-0 w-full h-full bg-dark-primary"
              style={{
                zIndex: "0",
              }}
            ></div>

            <div className="w-full lg:w-4/12 px-8 relative z-10">
              <div
                className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-lg bg-dark-card border border-dark-tertiary shadow-2xl"
                style={{
                  minHeight: "400px",
                }}
              >
                <div className="flex-auto px-4 lg:px-10 py-10 pt-8">
                  <div className="text-gray-100 text-center mb-8 font-bold text-2xl">
                    Log In
                  </div>

                  <form onSubmit={submitFormData} className="mt-6">
                    <div className="flex mb-6 bg-dark-input p-1 rounded-lg border border-dark-tertiary">
                      {['student', 'admin', 'labincharge'].map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setRole(r)}
                          className={`flex-1 py-2 rounded-md text-sm font-bold capitalize transition-all duration-200 ${role === r
                            ? "bg-accent-primary text-white shadow-md transform scale-[1.02]"
                            : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                            }`}
                        >
                          {r === 'labincharge' ? 'Lab Incharge' : r}
                        </button>
                      ))}
                    </div>

                    <div className="relative w-full mb-6">
                      <label className="block text-gray-300 text-sm font-bold mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={getFormData}
                          className="border border-dark-tertiary px-4 py-3 placeholder-gray-500 text-gray-100 bg-dark-input rounded-lg text-sm shadow focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary w-full ease-linear transition-all duration-150"
                          placeholder="Enter your email"
                        />
                      </div>
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div className="relative w-full mb-6">
                      <label className="block text-gray-300 text-sm font-bold mb-2">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type={showPassword ? "text" : "password"}
                        minLength={8}
                        maxLength={14}
                        name="password"
                        value={formData.password}
                        onChange={getFormData}
                        className="border border-dark-tertiary px-4 py-3 placeholder-gray-500 text-gray-100 bg-dark-input rounded-lg text-sm shadow focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary w-full ease-linear transition-all duration-150"
                        placeholder="Enter your password"
                      />
                      {errors.password && (
                        <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                      )}
                    </div>

                    <div className="flex items-center mb-6">
                      <input
                        type="checkbox"
                        id="showPassword"
                        checked={showPassword}
                        onChange={() => setShowPassword(!showPassword)}
                        className="form-checkbox text-accent-primary bg-dark-input border-dark-tertiary rounded-md"
                      />
                      <label
                        htmlFor="showPassword"
                        className="text-sm text-gray-400 ml-2"
                      >
                        Show Password
                      </label>
                    </div>

                    <div className="text-center mt-8">
                      <button
                        className="bg-accent-primary text-white active:bg-accent-hover text-sm font-bold uppercase px-6 py-3 rounded-lg shadow hover:shadow-lg hover:bg-accent-hover outline-none focus:outline-none w-full ease-linear transition-all duration-150"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? "Logging in..." : "Log In"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="flex flex-wrap mt-6 relative">
                <div className="w-1/2">
                  <Link
                    to="/auth/forgot-password"
                    className="text-gray-400 hover:text-accent-primary font-semibold text-xl transition-colors"
                  >
                    <small>Forgot password?</small>
                  </Link>
                </div>
                <div className="w-1/2 text-right">
                  <Link to="/auth/register" className="text-gray-400 hover:text-accent-primary font-semibold text-xl transition-colors">
                    <small>Create new account</small>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
